import React from 'react';
import { View } from '@smarterservices/smarter-ui';

/**
 * Video component for displaying video streams
 * 
 * @param {Object} props - Component props
 * @param {React.RefObject} props.streamRef - Reference to the video element
 * @param {boolean} props.mirror - Whether to mirror the video horizontally (default: false)
 * @param {boolean} props.muted - Whether the video should be muted (default: true)
 * @param {string} props.width - Width of the video element (default: 'auto')
 * @param {string} props.maxHeight - Maximum height of the video element (default: '40vh')
 * @param {string} props.objectFit - Object fit property for the video (default: 'contain')
 * 
 * @example
 * // Basic usage with a video stream
 * const videoRef = useRef(null);
 * 
 * // Later in your code, set the stream to the video element
 * useEffect(() => {
 *   if (videoRef.current && mediaStream) {
 *     videoRef.current.srcObject = mediaStream;
 *   }
 * }, [mediaStream]);
 * 
 * // In your render function
 * return <Video streamRef={videoRef} />;
 * 
 * @example
 * // Advanced usage with custom styling
 * return (
 *   <Video
 *     streamRef={videoRef}
 *     mirror={true}
 *     muted={false}
 *     width="100%"
 *     maxHeight="60vh"
 *     objectFit="cover"
 *   />
 * );
 */
const Video = ({
  streamRef,
  mirror = false,
  muted = true,
  width = '100%',
  objectFit = 'contain',
  autoPlay = true,
  onEnded = () => { },
  controls = false,
  ...props
}) => {
  return (
    <View as="div" width="100%" height="100%">
      <View
        as="div"
        display="inline-block"
        padding="0"
        margin="0"
        shadow="above"
        height="100%"
        borderRadius="medium"
        style={{
          overflow: 'hidden'
        }}
      >
        <video
          ref={streamRef}
          autoPlay={autoPlay}
          playsInline
          muted={muted}
          style={{
            width: '100%',
            height: '100%',
            objectFit,
            margin: '0',
            display: 'block',
            transform: mirror ? 'scaleX(-1)' : 'none' // Mirror the video if specified
          }}
          onEnded={onEnded}
          controls={controls}
          {...props}
        />
      </View>
    </View>
  );
};

export default Video;